// frontend/admin-portal/src/components/treasury/ExpectedProfitBreakdown.jsx
import React from "react";
import {
  Panel,
  Spinner,
  Alert,
  formatMoney,
  Badge,
} from "@fiberhome/shared-ui";
import ProfitBreakdownTabs from "./ProfitBreakdownTabs";

function Row({
  label,
  value,
  currency,
  tone = "default",
  bold = false,
  indent = 0,
}) {
  const color =
    tone === "success"
      ? "#198754"
      : tone === "danger"
        ? "#dc3545"
        : tone === "info"
          ? "#0dcaf0"
          : "var(--fh-text, #0f172a)";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.4rem 0",
        paddingLeft: indent * 16,
        fontWeight: bold ? 700 : 400,
        fontSize: bold ? 14 : 13,
        color: bold ? color : "inherit",
      }}
    >
      <span style={{ color: bold ? color : "var(--fh-text-muted, #6b7280)" }}>
        {label}
      </span>
      <span
        style={{
          fontWeight: bold ? 700 : 600,
          color,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatMoney(value, { symbol: currency })}
      </span>
    </div>
  );
}

export default function ExpectedProfitBreakdown({ data, loading, error }) {
  if (loading) {
    return (
      <Panel title="Expected Profit" tone="info">
        <div style={{ textAlign: "center", padding: "1rem 0" }}>
          <Spinner size="sm" />
        </div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel title="Expected Profit" tone="info">
        <Alert variant="danger">{error}</Alert>
      </Panel>
    );
  }

  if (!data) {
    return (
      <Panel title="Expected Profit" tone="info">
        <div className="fh-dashboard-empty">No inventory data</div>
      </Panel>
    );
  }

  const cur = data.currency;
  const isPositive = data.expectedProfit >= 0;

  return (
    <Panel
      title="Expected Profit"
      subtitle="If current stock is sold at list price"
      tone="info"
      actions={
        <Badge variant={isPositive ? "info" : "danger"}>
          {data.marginPct.toFixed(1)}% margin
        </Badge>
      }
    >
      <div style={{ marginBottom: "0.5rem" }}>
        <div style={{ fontSize: 11.5, color: "var(--fh-text-muted, #6b7280)" }}>
          Unrealized profit in stock
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 700,
            color: isPositive ? "#0dcaf0" : "#dc3545",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatMoney(data.expectedProfit, { symbol: cur })}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--fh-text-muted, #6b7280)",
            marginTop: 2,
          }}
        >
          {data.totalUnits} units · {data.productCount} products
        </div>
      </div>

      <div
        style={{
          borderTop: "1px solid var(--fh-border, #eef0f3)",
          paddingTop: "0.5rem",
        }}
      >
        <Row
          label="Expected Revenue"
          value={data.expectedRevenue}
          currency={cur}
          tone="info"
        />
        <Row
          label="Expected Cost"
          value={-data.expectedCost}
          currency={cur}
          tone="danger"
          indent={1}
        />
        <div
          style={{
            borderTop: "1px solid var(--fh-border, #eef0f3)",
            marginTop: "0.35rem",
          }}
        >
          <Row
            label="Expected Profit"
            value={data.expectedProfit}
            currency={cur}
            bold
            tone={isPositive ? "success" : "danger"}
          />
        </div>
      </div>

      {/* ── NEW: category / brand / item breakdown ──────── */}
      {data.breakdown && (
        <div
          style={{
            marginTop: "0.75rem",
            paddingTop: "0.75rem",
            borderTop: "1px solid var(--fh-border, #eef0f3)",
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: "var(--fh-text-muted, #6b7280)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "0.35rem",
            }}
          >
            Breakdown
          </div>
          <ProfitBreakdownTabs
            breakdown={data.breakdown}
            currency={cur}
            emptyMessage="No inventory in stock"
          />
        </div>
      )}

      {data.topProducts && data.topProducts.length > 0 && (
        <details style={{ marginTop: "0.75rem", fontSize: 12 }}>
          <summary
            style={{
              cursor: "pointer",
              color: "var(--fh-text-muted, #6b7280)",
              fontWeight: 600,
            }}
          >
            Top 5 contributors
          </summary>
          <div style={{ paddingTop: "0.35rem" }}>
            {data.topProducts.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.35rem 0",
                  borderTop: "1px dashed var(--fh-border, #eef0f3)",
                  fontSize: 12,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 200,
                    }}
                    title={p.name}
                  >
                    {p.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--fh-text-muted, #6b7280)",
                    }}
                  >
                    {p.stock} × {formatMoney(p.price ?? 0, { symbol: cur })}
                  </div>
                </div>
                <div
                  style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}
                >
                  <div style={{ fontWeight: 700, color: "#198754" }}>
                    {formatMoney(p.expectedProfit, { symbol: cur })}
                  </div>
                  <div
                    style={{
                      fontSize: 10.5,
                      color: "var(--fh-text-muted, #6b7280)",
                    }}
                  >
                    {p.marginPct}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </Panel>
  );
}
